import { getSettings } from '../../api/emailsApi/settings';
import { getUser, getAuthStatus } from '../../api/emailsApi/users';
import { getAttachments, getEmail } from '../../api/emailsApi/emails';
import { getTimeConfig } from '../../api/emailsApi/time';
import { handleLogout } from '../utils/auth';
import * as RootActions from '../actions/index';
import * as MailActions from '../actions/mail';
import * as CalendarActions from '../actions/calendar';
import * as FileActions from '../actions/files';
import * as AuthActions from '../actions/auth';
import * as TimeActions from '../actions/time';
import * as SalesforceRecordActions from '../actions/salesforceRecords';
import officeApi from '../../api/officeApi';
import { getIcons } from '../../api/emailsApi/icons';
import { batch } from 'react-redux';
import { getAttachmentsWithCache, processDocrioAndOutlookAttachments, processPresavedAndOutlookAttachments } from './emails';
import { getProcessingTimeRecords } from './time';
import { setLocalStorageWithExpiry, getLocalStorageWithExpiry, removeLocalStorageKey, THIRTY_MINUTES_INT } from './browser.js';
import { isDesktop } from './environment';
import tokenManager from './tokenManager';
import { getCalendarEvent, getRecentlyViewed, getSalesforceContacts, resyncEvents } from '../../api/emailsApi/calendar';
import calendarEventStatus from './calendarEventStatus';

const ICONS_CACHE_STRING = 'docrioEmailsIcons';
const SETTINGS_CACHE_STRING = 'settings';
const USERS_CACHE_STRING = 'users';
const TIME_CACHE_STRING = 'time';
const CACHED_OBJECT_FORM_FIELDS_STRING = 'cachedObjectFormFields';
const DISPLAY_FIELDS_CACHE_STRING = 'displayFields';

const FIVE_MINUTES_INT = 300000;
const FOUR_HOURS_INT = 14400000;
const ONE_YEAR_INT = 31536000000;

const getAndParseCache = (cachedString, dispatchAction, dispatch) => {
  const cache = getLocalStorageWithExpiry(cachedString);
  const cachedData = cache && JSON.parse(cache);
  if (cachedData && dispatchAction) {
    dispatch(dispatchAction(cachedData));
  }
  return cachedData;
};

const getHeaderValueCaseInsensitive = (headers, headerKey) => {
  return headers?.[headerKey] || Object.entries(headers || {}).find(([key, _]) => key.toLowerCase() === headerKey)?.[1];
};

const isUserAuthenticated = async (dispatch, reject) => {
  try {
    const authStatus = await getAuthStatus();
    return authStatus;
  } catch (error) {
    dispatch(AuthActions.authFailure());
    reject(error);
  }
};

const getUserInfo = async (dispatch, reject, itemType) => {
  dispatch(AuthActions.retrieveUserRequest());
  let userInfo;

  // make sure the outlook user email and the current user email match
  const outlookUser = await officeApi.getUserInfo().emailAddress;
  try {
    userInfo = getAndParseCache(USERS_CACHE_STRING, AuthActions.retrieveUserSuccess, dispatch);
    if (!userInfo || (userInfo.email !== outlookUser && itemType === 'appointment')) {
      const userResponse = await getUser();
      userInfo = userResponse.data;
      setLocalStorageWithExpiry(USERS_CACHE_STRING, JSON.stringify(userInfo), FIVE_MINUTES_INT);
      dispatch(AuthActions.retrieveUserSuccess(userInfo));
    }
    return userInfo;
  } catch (error) {
    dispatch(RootActions.setInitializationError(true));
    reject(error);
  }
};

const getEmailInfo = async (itemId, defaultObjectApiName, ownerEmail, dispatch, reject) => {
  dispatch(MailActions.getEmail());
  const tokenId = tokenManager.addToken();
  let emailResponse;
  let cachedObjectDetails;
  try {
    const cacheKey = `litifyobj#${defaultObjectApiName}`;
    cachedObjectDetails = getLocalStorageWithExpiry(cacheKey);
    emailResponse = await getEmail(itemId, defaultObjectApiName, !!cachedObjectDetails, ownerEmail);
    const backendVersion = getHeaderValueCaseInsensitive(emailResponse?.headers, 'docrio-email-client-version');
    if (backendVersion) {
      if (`v${backendVersion}` !== process.env.VERSION) {
        dispatch(AuthActions.updateAvailable());
        throw new Error('Email client version mismatch');
      }
    }
    const initialEmailAutoThreadedRecords = emailResponse?.data?.initialEmailAutoThreadedRecords;
    if (initialEmailAutoThreadedRecords) {
      dispatch(MailActions.setInitialEmailAutoThreadedRecords(initialEmailAutoThreadedRecords));
    }
    if (emailResponse.status === 202) {
      emailResponse.data = cachedObjectDetails; // set emailResponse.data from cache
    } else if (!emailResponse.data.EmailMessage && !emailResponse.data.Results && !emailResponse.data.preSave) {
      const toBeCached = {
        AllRecords: emailResponse?.data?.AllRecords || [],
        ListViewRecords: emailResponse?.data?.ListViewRecords || [],
      };
      setLocalStorageWithExpiry(cacheKey, toBeCached, THIRTY_MINUTES_INT);
    }
  } catch (error) {
    if (error.response?.status === 500) {
      if (error.response.data.Message === 'Error querying exchangeUserByEmail for ownerEmail') {
        dispatch(MailActions.setDelegateError(true));
      }
    } else {
      dispatch(RootActions.changeAlertText('Error loading. Please refresh', 'error'));
    }
    dispatch(RootActions.setInitializationError(true));
    reject(error);
  }

  const emailAndDisplayRecords = emailResponse.data;
  let responseRecordsFromSF = [];

  if (emailAndDisplayRecords.EmailMessage) { // Pre emails v5 SF response
    responseRecordsFromSF = [emailAndDisplayRecords.EmailMessage];
  } else if (emailAndDisplayRecords.Results) { // emails v5 SF response
    responseRecordsFromSF = Object.values(emailAndDisplayRecords.Results);
  }
  const savedStatus = !!emailAndDisplayRecords?.isSavingToTimeline || !!responseRecordsFromSF?.find((result) => result.emailMessageRecordSaved);
  if (savedStatus) {
    emailAndDisplayRecords.ListViewRecords = [];
    emailAndDisplayRecords.AllRecords = cachedObjectDetails?.AllRecords || responseRecordsFromSF.map((records) => records?.displayRecords[0]) || [];
  }

  if (tokenManager.getTokenValue(tokenId)) {
    tokenManager.removeToken(tokenId);
    throw Error('Token cancelled');
  }
  if (emailAndDisplayRecords.preSave) {
    const savedStatusObject = {};
    const selectedRecords = [];
    emailAndDisplayRecords?.relatedTos.forEach((relatedTo) => {
      savedStatusObject[relatedTo.id] = relatedTo.isSavingToTimeline;
      selectedRecords.push({ ...relatedTo, Id: relatedTo.id, autothreading: relatedTo.threadSave, ...relatedTo.displayFields });
    });
    batch(() => {
      dispatch(MailActions.selectRecords(selectedRecords));
      dispatch(MailActions.activityTimelineSaved(savedStatusObject));
    });
  } else {
    const savedStatusObject = {};
    const selectedRecords = [];
    if (responseRecordsFromSF.length) {
      responseRecordsFromSF.forEach((relatedTo) => {
        if (relatedTo.id && relatedTo.displayRecords?.length) {
          savedStatusObject[relatedTo.id] = relatedTo.emailMessageRecordSaved;
          selectedRecords.push({ ...relatedTo, Id: relatedTo.id, type: relatedTo.relatedToApiName, autothreading: emailAndDisplayRecords.threadSave?.includes(relatedTo.id), ...relatedTo.displayRecords[0] }); // there is only display record here i think
        }
      });
      dispatch(MailActions.selectRecords(selectedRecords));
    }
    dispatch(MailActions.emailSavedStatus(emailAndDisplayRecords, savedStatusObject));
  }

  tokenManager.removeToken(tokenId);
  return emailAndDisplayRecords;
};

const getTimeRecords = async (emailIds, timeConfig, settings, dispatch, ownerEmail) => {
  const timeNamespace = settings?.override?.timeNamespace || 'litify_time';
  try {
    const timeRecords = await getProcessingTimeRecords(emailIds, null, timeNamespace, dispatch, ownerEmail);
    await setSavedTimeRecords(timeRecords, timeConfig, settings, dispatch);
  } catch (err) {
    console.error(err);
    dispatch(TimeActions.isErrorLoadingTimeRecords(true));
    return;
  }
};

const setSavedTimeRecords = async (timeRecords, timeConfig, settings, dispatch) => {
  const timeNamespace = settings?.override?.timeNamespace || 'litify_time';
  try {
    const taskCodes = (timeConfig?.fields?.find((field) => field.name === `${timeNamespace}__lit_Task_Code__c`))?.value;
    for (const record of timeRecords) {
      record.saved = true;
      if (taskCodes) {
        if (record[`${timeNamespace}__lit_Task_Code__c`]) {
          record[`${timeNamespace}__lit_Task_Code__c`] = (taskCodes.find((code) => code.Id === record[`${timeNamespace}__lit_Task_Code__c`]))?.Name || '';
        }
      }
    }
    dispatch(TimeActions.setTimeRecords(timeRecords));
  } catch (err) {
    console.error(err);
    dispatch(TimeActions.isErrorLoadingTimeRecords(true));
    return;
  }
};

const getCalendarEventInfo = async (isComposingEvent, itemId, defaultObjectApiName, dispatch, reject, attendeesEmails, organizerEmail, user, start, subject, isAllDay) => {
  const tokenId = tokenManager.addToken();
  let calendarResponse;
  try {
    const cacheKey = `litifyobj#${defaultObjectApiName}`;
    const cachedObjectDetails = getLocalStorageWithExpiry(cacheKey);
    const attendees = await officeApi.getEventAttendeesWithNames();
    const sharedProperties = await officeApi.getSharedProperties(attendees);
    let ownerEmail = sharedProperties?.owner;
    const ownerForDelegatedCalendar = sharedProperties?.eventAttendeesForDelegatedCalendar?.find((attendee) => attendee.emailAddress === ownerEmail) || { emailAddress: ownerEmail };
    dispatch(CalendarActions.setOwnerForDelegatedCalendar(ownerForDelegatedCalendar));
    // if the owner is the current user, we don't need to pass the owner email
    if (ownerEmail === user.email) {
      ownerEmail = null;
    }

    if (user.isCalendarUser) {
      try {
        const response = await resyncEvents(ownerEmail);
        if (response?.status === 202 || response?.status === 200) {
          dispatch(CalendarActions.setResyncJobId(response.data.jobId));
        }
      } catch (e) {
        console.error(e);
        dispatch(RootActions.changeAlertText('Error starting calendar resync, refresh add-in to try again', 'error'));
      }
    }

    try {
      if (itemId) {
        calendarResponse = await getCalendarEvent(itemId, ownerEmail, defaultObjectApiName, !!cachedObjectDetails);
      } else {
        const rawStartString = start.toISOString();
        const startString = isAllDay ? `${rawStartString.split('T')[0]}T00:00:00.000Z` : rawStartString;
        calendarResponse = await getRecentlyViewed(defaultObjectApiName, ownerEmail, startString, subject);
      }
    } catch (error) {
      if (error.response?.status === 503) {
        await handleLogout(dispatch);
        dispatch(AuthActions.forcedUserLogOut(true));
        return;
      }
      if (error.response?.status === 401) {
        dispatch(CalendarActions.isCalendarUser(false));
        dispatch(CalendarActions.delegateError(true));
        return;
      }
    }
    if (!calendarResponse.data.CalendarEvent?.currentUser && !calendarResponse.data.currentUser) {
      throw new Error('No current user found');
    }
    const backendVersion = getHeaderValueCaseInsensitive(calendarResponse?.headers, 'docrio-email-client-version');
    if (backendVersion) {
      if (`v${backendVersion}` !== process.env.VERSION) {
        dispatch(AuthActions.updateAvailable());
        throw new Error('Email client version mismatch');
      }
    }
    if (calendarResponse.status === 207) {
      // 207 indicates that the cache should be used, but that we also need
      // to pull in all of the attendees from the response
      calendarResponse.data = { ...cachedObjectDetails, ...calendarResponse?.data }; // set calendarResponse.data from cache
    } else if (!calendarResponse.data.CalendarEvent) {
      const toBeCached = {
        AllRecords: calendarResponse?.data?.AllRecords || [],
        ListViewRecords: calendarResponse?.data?.ListViewRecords || [],
      };
      setLocalStorageWithExpiry(cacheKey, toBeCached, THIRTY_MINUTES_INT);
    }
  } catch (error) {
    console.error(error);
    throw new Error(error);
  }

  const calendarEventAndDisplayRecords = calendarResponse.data;
  const savedStatus = !!calendarEventAndDisplayRecords.CalendarEvent;
  const salesforceEventId = calendarEventAndDisplayRecords?.salesforceEventId;
  const isPrivateEventSF = calendarEventAndDisplayRecords?.isPrivate;
  let attendeeMapping = {};
  if (isComposingEvent) {
    // call out to get the people
    try {
      attendeeMapping = await getSalesforceContacts(attendeesEmails);
      dispatch(CalendarActions.setSalesforceContactMap(attendeeMapping));
    } catch (error) {
      console.error(error);
      throw new Error('Error fetching attendees. Please try again.');
    }
  }

  // if the event has been saved, we need to update the attendees
  if (savedStatus) {
    calendarEventAndDisplayRecords.ListViewRecords = [];
    calendarEventAndDisplayRecords.AllRecords = [calendarEventAndDisplayRecords.CalendarEvent?.salesforceRelatedToObject?.displayFields];
    const errorMessage = calendarResponse.data?.CalendarEvent?.salesforceRelatedToObject?.errorMessage;
    let organizer = calendarResponse.data?.CalendarEvent?.organizer[0];

    // if the organizer email matches the current user but the salesforce user id does not, we need to update the organizer and record an attendee change
    if (organizer.emailAddress === calendarResponse.data?.CalendarEvent?.currentUser?.emailAddress && organizer.objectId !== calendarResponse.data?.CalendarEvent?.currentUser?.objectId) {
      organizer = calendarResponse.data.CalendarEvent.currentUser;
      dispatch(CalendarActions.attendeeChange());
    }

    if (errorMessage) {
      throw new Error(errorMessage);
    }

    const formattedAttendees = calendarResponse.data?.CalendarEvent?.attendees?.map((attendee) => ({
      id: attendee.objectId,
      email: attendee.emailAddress,
    }));
    batch(() => {
      dispatch(CalendarActions.selectOrganizer(organizer));
      dispatch(CalendarActions.setSavedEventAttendees(formattedAttendees));
      !isComposingEvent && dispatch(CalendarActions.setSalesforceContactMap(calendarResponse.data?.CalendarEvent?.salesforceAttendeeMapping));
    });
  } else {
    const multipleMatchedOrganizers = calendarResponse.data.organizer?.length > 1;
    if (multipleMatchedOrganizers) {
      const matchedOrganizers = {};
      matchedOrganizers[organizerEmail] = calendarResponse.data.organizer;
      dispatch(CalendarActions.setMatchedOrganizers(matchedOrganizers));
    } else {
      dispatch(CalendarActions.selectOrganizer(calendarResponse.data.organizer?.[0] || calendarResponse.data.currentUser));
    }
    !isComposingEvent && dispatch(CalendarActions.setSalesforceContactMap(calendarResponse.data.salesforceAttendeeMapping));

    // If organizer is populated, then we need to add current user as an attendee
    if (!isComposingEvent && calendarResponse.data.organizer?.length > 0) {
      dispatch(CalendarActions.setSalesforceContactMap({ ...calendarResponse.data.salesforceAttendeeMapping, [calendarResponse.data.currentUser.emailAddress]: [calendarResponse.data.currentUser] }));
    }
  }
  if (salesforceEventId) {
    dispatch(CalendarActions.setSalesforceEventId(salesforceEventId));
  }
  if (isPrivateEventSF) {
    dispatch(CalendarActions.isPrivateEventSF(isPrivateEventSF));
  }

  if (tokenManager.getTokenValue(tokenId)) {
    tokenManager.removeToken(tokenId);
    throw Error('Token cancelled');
  }

  if (calendarEventAndDisplayRecords?.eventStatus === calendarEventStatus.preSaved) {
    dispatch(CalendarActions.eventPresave(calendarEventAndDisplayRecords.CalendarEvent));
  } else {
    dispatch(CalendarActions.calendarEventSavedStatus(calendarEventAndDisplayRecords, savedStatus));
  }

  tokenManager.removeToken(tokenId);
  return calendarEventAndDisplayRecords;
};


const getIconsPromise = async (objectApiNames, dispatch) => {
  try {
    const icons = (await getIcons(objectApiNames))?.data;
    if (!icons) {
      return;
    }
    dispatch(MailActions.loadIcons(icons));
    setLocalStorageWithExpiry(ICONS_CACHE_STRING, JSON.stringify(icons), FIVE_MINUTES_INT);
    return icons;
  } catch (e) {
    console.error(e);
  }
};

const loadIcons = async (settings, dispatch) => {
  if (!settings?.objectsAndDisplayFields?.length) {
    return;
  }
  const objectApiNames = ['Contact', 'User', 'Task'].concat(settings?.objectsAndDisplayFields.map((objectDisplayField) => objectDisplayField?.apiName));
  const cachedIcons = getAndParseCache(ICONS_CACHE_STRING, MailActions.loadIcons, dispatch);
  if (!cachedIcons) {
    return await getIconsPromise(objectApiNames, dispatch);
  }
};

const handleSettingsChange = (settings) => {
  const oldSettings = getLocalStorageWithExpiry(DISPLAY_FIELDS_CACHE_STRING)?.value || [];
  const newSettings = settings?.objectsAndDisplayFields?.sort((a, b) => a.apiName - b.apiName);
  if (JSON.stringify(oldSettings) === JSON.stringify(newSettings)) {
    return;
  }
  for (const df of oldSettings) {
    removeLocalStorageKey(df.apiName);
  }
  setLocalStorageWithExpiry(DISPLAY_FIELDS_CACHE_STRING, newSettings, ONE_YEAR_INT);
};

const loadSettingsToState = async (dispatch, reject) => {
  let settings;
  try {
    settings = getAndParseCache(SETTINGS_CACHE_STRING, MailActions.loadSettings, dispatch);
    if (!settings) {
      const settingsResp = await getSettings();
      settings = settingsResp.data;
      dispatch(MailActions.loadSettings(settings));
      setLocalStorageWithExpiry(SETTINGS_CACHE_STRING, JSON.stringify(settings), FIVE_MINUTES_INT);
      handleSettingsChange(settings);
    }

    // if the user has disabled calendar sync, we need to set the calendar sync disabled flag
    if (settings?.calendarSettings?.calendarSyncType === 'sync_disabled') {
      dispatch(CalendarActions.syncDisabled(true));
    }
    if (settings.autoThreading?.enableAllUsers) {
      dispatch(MailActions.setAutoThreadingDefault(true));
    }
  } catch (error) {
    console.log(error);
    dispatch(RootActions.setInitializationError(true));
    reject(error);
  }
  loadIcons(settings, dispatch);
  if (settings.autoThreading?.enableAllUsers) {
    dispatch(MailActions.setAutoThreadingDefault(true));
  }

  return settings;
};

const loadTimeConfigToState = async (timeEnabled, dispatch) => {
  if (!timeEnabled) {
    return;
  }
  let config;
  try {
    config = getAndParseCache(TIME_CACHE_STRING, TimeActions.loadConfig, dispatch);
    if (!config) {
      const timeConfigResp = await getTimeConfig();
      config = timeConfigResp.data;
      dispatch(TimeActions.loadConfig(config));
      setLocalStorageWithExpiry(TIME_CACHE_STRING, JSON.stringify(config), FOUR_HOURS_INT);
    }
  } catch (error) {
    console.error(error);
    dispatch(TimeActions.isErrorLoadingTimeConfig(true));
    return;
  }

  return config;
};

export const appInitialization = async (dispatch) => {
  await new Promise((resolve, reject) => {
    officeApi.onInit(async (item) => {
      switch (item.itemType) {
        case 'message':
          dispatch(RootActions.emailSelected());
          resolve();
          break;
        case 'appointment':
          dispatch(RootActions.calendarSelected());
          resolve();
          break;
        default:
          reject(new Error(`Invalid item type ${item.itemType}`));
          break;
      }
    });
  });
};

export const retrieveDataForAppInitialization = async (dispatch) => {
  // remove azureToken from local storage so it can be refreshed
  localStorage.removeItem('azureToken');
  await new Promise((resolve, reject) => {
    officeApi.onMessageLoaded(async (item) => {
      const tokenId = tokenManager.addToken();
      let userIsAuthenticated;
      try {
        dispatch(RootActions.changeEmail());
        dispatch(RootActions.officeUninitialized());
        const [userAuthenticated, settings, user] = await Promise.all([isUserAuthenticated(dispatch, reject), loadSettingsToState(dispatch, reject), getUserInfo(dispatch, reject, item?.itemType)]);
        userIsAuthenticated = userAuthenticated;
        if (!settings?.objectsAndDisplayFields.length) {
          batch(() => {
            dispatch(RootActions.changeAlertText('No default objects are configured', 'error'));
            dispatch(RootActions.setInitializationError(true));
          });
          reject(new Error('Error - no default objects configured'));
        }

        if (item.itemType === 'message') {
          return await handleEmailInitialization(dispatch, resolve, reject, item, settings, user, tokenId);
        } else if (item.itemType === 'appointment') {
          // This is to make sure the that add-in is in focus whe it is opened
          const tabKeyPress = new KeyboardEvent('keydown', { key: 'Tab' });
          document.dispatchEvent(tabKeyPress);
          if (!user.isCalendarUser) {
            batch(() => {
              dispatch(RootActions.calendarSelected());
              dispatch(RootActions.setInitializationError(false));
              dispatch(AuthActions.loginSuccess());
              dispatch(RootActions.officeInitialized());
            });
            return;
          }
          batch(() => {
            dispatch(RootActions.calendarSelected());
            dispatch(CalendarActions.isCalendarUser(true));
          });
          return await handleCalendarInitialization(dispatch, resolve, reject, item, settings, user, tokenId);
        }
      } catch (error) {
        console.error(error);
        tokenManager.removeToken(tokenId);
        if (error.message !== 'Token cancelled') {
          batch(() => {
            dispatch(RootActions.setInitializationError(true));
            dispatch(RootActions.officeInitialized());
            // Needed to show log out button on error screen
            if (userIsAuthenticated) {
              dispatch(AuthActions.loginSuccess());
            }
            dispatch(RootActions.changeAlertText('Unable to retrieve data', 'error'));
          });
        }
        reject(error);
      }
    });
  });
};

const handleCalendarInitialization = async (dispatch, resolve, reject, item, settings, user, tokenId) => {
  const apiName = settings?.objectPickerDefaults?.[user?.salesforceProfileId]?.apiName || settings?.objectsAndDisplayFields[0]?.apiName;
  const cachedObjectFormFields = await checkForCachedObjectFormFields();

  let subject = '';
  let start;
  let end;
  let location;
  let recurrence;
  let itemId;
  let required;
  let optional;
  let organizer;

  // Web and Mac desktop appear to have the item at this point, while Windows Desktop
  // does not. We check again to see if it actually exists. In all three cases, the id
  // that is returned appears to be of the same type (restId?). Do note that the id for
  // an individual event and series master are different from a series instance. A series
  // instance includes a (/) character that MUST be replaced with a (-). See the example:
  // AAMkAGFlYjM4MDM5LTljZTEtNDJiYy05NTY0LTdjNjdjNmU5NTk2NwFRAAgI2wlna+yAAEYAAAAADSI1el1njUGfSczQOd8G8QcAmkqPWPU9UkaaaibXubdy+gAAAAABDQAAmkqPWPU9UkaaaibXubdy+gAAwz-hbQAAEA==
  if (!item.itemId) {
    itemId = await officeApi.getItemIdAsync(); // This is needed for Windows Outlook
    [subject, start, end, location, recurrence, required, optional, organizer] = await Promise.all([
      officeApi.getEventSubject(),
      officeApi.getEventStart(),
      officeApi.getEventEnd(),
      officeApi.getEventLocation(),
      officeApi.getEventRecurrence(),
      ...officeApi.getComposeEventAttendees(),
    ]);
  } else {
    subject = item.subject;
    start = new Date(item.start);
    end = new Date(item.end);
    location = item.location;
    recurrence = item.recurrence;
    [required, optional, organizer] = officeApi.getEventAttendees();
  }

  const timeDifference = end.getTime() - start.getTime();
  const isAllDay = timeDifference === 24 * 60 * 60 * 1000;

  // I really don't love this object model (would rather flatten), but otherwise we'd need
  // too await the async call again when running officeapi.removeEmailFromEvent. For now,
  // the flattening is taking place in Attendees when rendering to prevent duplicates,
  // and handle the fact that Windows makes the organizer a required attendee behind the scenes
  const eventAttendees = {
    organizer,
    required: Array.from(new Set([...required])),
    optional: Array.from(new Set([...optional])),
  };

  const isComposingEvent = !item.itemId;
  const eventId = item.itemId || itemId ? officeApi.getItemRestId(item.itemId || itemId) : null;
  await getCalendarEventInfo(isComposingEvent, eventId, apiName, dispatch, reject, [...required, ...optional, organizer], organizer, user, start, subject, isAllDay);
  // if the associated token is cancelled, we do not do any dispatches.
  const tokenValue = tokenManager.getTokenValue(tokenId);
  if (!tokenValue) {
    batch(() => {
      dispatch(RootActions.setInitializationError(false));
      dispatch(AuthActions.loginSuccess());
      dispatch(RootActions.officeInitialized());

      if (isComposingEvent) {
        dispatch(CalendarActions.composingEvent(eventId));
      } else {
        dispatch(CalendarActions.eventLoaded(eventId));
      }

      // subject may come back as null if no title was added, must set to empty string to prevent add-in from bombing out
      dispatch(CalendarActions.setEventSubject(subject || ''));
      dispatch(CalendarActions.setEventStart(start));
      dispatch(CalendarActions.setEventEnd(end));
      dispatch(CalendarActions.setEventLocation(location));
      dispatch(CalendarActions.setEventRecurrence(recurrence));
      dispatch(CalendarActions.setEventAttendees(eventAttendees));
      dispatch(CalendarActions.setEventAllDay(isAllDay));
      dispatch(SalesforceRecordActions.setCachedObjectFieldSets(cachedObjectFormFields));
    });
  }
  tokenManager.removeToken(tokenId);
  resolve();
};

const handleEmailInitialization = async (dispatch, resolve, reject, item, settings, user, tokenId) => {
  dispatch(RootActions.emailSelected());
  const objectsAndDisplayFields = settings.objectsAndDisplayFields || [];
  const apiName = settings.objectPickerDefaults?.[user?.salesforceProfileId]?.apiName || objectsAndDisplayFields[0]?.apiName || '';
  const minimumFileSize = settings?.docrioSettings?.attachments?.minimumFileSize;
  const timeEnabled = settings?.timeSettings?.timeEnabled && user.timeAccess;
  const timeConfigPromise = loadTimeConfigToState(timeEnabled, dispatch);
  const cachedObjectFormFields = await checkForCachedObjectFormFields();

  const sharedProperties = await officeApi.getSharedProperties([]);
  let ownerEmail = sharedProperties?.owner;

  // Extract owner display name based on users attached to email.
  let ownerDisplayName;
  if (item.from?.emailAddress?.toLowerCase() === ownerEmail?.toLowerCase()) {
    ownerDisplayName = item.from?.displayName;
  } else {
    if (Array.isArray(item.to)) {
      ownerDisplayName = item.to.find((recipient) => recipient.emailAddress?.toLowerCase() === ownerEmail?.toLowerCase())?.displayName;
    } else if (Array.isArray(item.cc)) {
      ownerDisplayName = item.cc.find((recipient) => recipient.emailAddress?.toLowerCase() === ownerEmail?.toLowerCase())?.displayName;
    }
  }
  // if the owner is the current user, we don't need to pass the owner email or owner display name
  if (ownerEmail === user.email) {
    ownerEmail = null;
    ownerDisplayName = null;
  }

  if (user.isCalendarUser) {
    try {
      const response = await resyncEvents(ownerEmail);
      if (response?.status === 202 || response?.status === 200) {
        dispatch(CalendarActions.setResyncJobId(response.data.jobId));
      }
    } catch (e) {
      console.error(e);
      dispatch(RootActions.changeAlertText('Error starting calendar resync, refresh add-in to try again', 'error'));
    }
  }

  dispatch(MailActions.setOwnerEmail(ownerEmail));
  dispatch(MailActions.setOwnerDisplayName(ownerDisplayName));

  let emailAndDisplayRecords;
  let subject = '';
  let docrioSubject = undefined;
  let emlSubject = undefined;
  let emailMessageRecordName = undefined;
  let body = '';
  let attachments = [];
  let draftId;
  let timeRecords;
  if (!item.itemId) {
    const [composeSubject, ewsDraftId, composeBody] = await Promise.all([officeApi.getSubject(), officeApi.saveEmail(), officeApi.getBody()]);
    (composeSubject === '' && isDesktop()) && await officeApi.setSubject('(No Subject)');
    draftId = await officeApi.getItemRestId(ewsDraftId);
    if (draftId.includes('clientid:')) {
      // temp fix reload add in if the draft id is not valid reload
      window.location.reload();
    }
    emlSubject = composeSubject;
    body = composeBody;
    let outlookAttachmentsWithHashes;
    [outlookAttachmentsWithHashes, emailAndDisplayRecords] = await Promise.all([getAttachmentsWithCache(draftId, true, minimumFileSize, ownerEmail, dispatch), getEmailInfo(draftId, apiName, ownerEmail, dispatch, reject)]);
    attachments = [...outlookAttachmentsWithHashes, { name: composeSubject, id: draftId, body, folderPaths: {} }];

    // check for presave
    if (emailAndDisplayRecords.preSave) {
      // setting the apiName to all the relatedTos
      if (emailAndDisplayRecords?.relatedTos) {
        emailAndDisplayRecords.relatedTos = emailAndDisplayRecords.relatedTos.map((relatedTo) => ({
          ...relatedTo,
          relatedToObjectName: objectsAndDisplayFields.find((object) => object?.apiName === relatedTo?.type)?.label || '',
        }));
      }
      subject = emailAndDisplayRecords?.relatedTos[0]?.emailSubject || ''; // currently all relatedTos will have the same email subject
      const processedAttachments = await processPresavedAndOutlookAttachments(outlookAttachmentsWithHashes, draftId, emailAndDisplayRecords.relatedTos);
      attachments = processedAttachments;
      // if we are saving 1 related to to the timeline, we are saving all of them
      dispatch(MailActions.savingToTimeline(!!emailAndDisplayRecords?.relatedTos[0]?.isSavingToTimeline));

      // handle time records
      if (timeEnabled) {
        // Get time records for old schema and new schema presave records
        const timeRecords = emailAndDisplayRecords?.timeRecords?.length ? emailAndDisplayRecords.timeRecords : emailAndDisplayRecords.relatedTos.map((relatedRecord) => relatedRecord.timeRecords).flat();
        if (timeRecords?.length) {
          const timeConfig = await timeConfigPromise;
          await setSavedTimeRecords(timeRecords, timeConfig, settings, dispatch);
        }
      }
    }
  } else {
    let getAttachmentsResp;
    [emailAndDisplayRecords, getAttachmentsResp] = await Promise.all([getEmailInfo(item.itemId, apiName, ownerEmail, dispatch, reject), getAttachments(item.itemId, ownerEmail, false)]);

    const MAX_RETRIES = 20;
    let retryCount = 0;
    if (getAttachmentsResp.jobStatus === 'pending') {
      dispatch(RootActions.changeAlertText('Handling large attachments, please wait.'));
      const backgroundJob = getAttachmentsResp.backgroundJobId;

      if (backgroundJob) {
        while (getAttachmentsResp.jobStatus === 'pending') {
          retryCount += 1;
          if (retryCount > MAX_RETRIES) {
            dispatch(RootActions.changeAlertText('Failed to retrieve attachments after multiple attempts.', 'error'));
            break;
          }
          getAttachmentsResp = await getAttachments(item.itemId, ownerEmail, false, backgroundJob);
          // timeout to slow down the retries
          await new Promise((resolve) => setTimeout(resolve, 1000 * retryCount));
        }
      }
    } else if (getAttachmentsResp.attachmentsError) {
      dispatch(RootActions.changeAlertText('Failure to upload file(s), exceeds add-in limit. Upload in Docrio.', 'error'));
    }
    emlSubject = item.subject;
    if (emailAndDisplayRecords?.EmailMessage || emailAndDisplayRecords?.Results) {
      let savedRecordsFromSF = emailAndDisplayRecords?.Results ? Object.values(emailAndDisplayRecords.Results) : [emailAndDisplayRecords.EmailMessage];
      if (timeEnabled) {
        const emailMessageIds = emailAndDisplayRecords?.EmailMessage?.emailMessageId ? [emailAndDisplayRecords?.EmailMessage?.emailMessageId] : savedRecordsFromSF.map((relatedRecord) => relatedRecord.emailMessageId);
        if (emailMessageIds?.length) {
          const timeConfig = await timeConfigPromise;
          timeRecords = getTimeRecords(emailMessageIds, timeConfig, settings, dispatch, ownerEmail);
        }
      }
      savedRecordsFromSF = savedRecordsFromSF.map((relatedRecord) => {
        // setting the apiName to all the attachments
        const attachments = (relatedRecord?.attachments?.map((attachment) => ({
          ...attachment,
          relatedToObjectName: objectsAndDisplayFields.find((object) => object?.apiName === relatedRecord?.relatedToApiName)?.label || '',
        })) || []);
        return attachments.length ? { ...relatedRecord, attachments } : relatedRecord;
      });
      const docrioFiles = (savedRecordsFromSF.map((relatedRecord) => relatedRecord.attachments).flat() || []).filter(Boolean);
      const processedAttachments = await processDocrioAndOutlookAttachments(getAttachmentsResp, docrioFiles, item.itemId, item.subject, settings?.override?.docrioNamespace || 'litify_docs');
      attachments = processedAttachments;
      docrioSubject = processedAttachments.find((file) => file.id === item.itemId).name;
      emailMessageRecordName = savedRecordsFromSF[0]?.emailMessageRecordName; // all emails should have the same name
      await timeRecords;
    } else {
      const emlHash = getAttachmentsResp.emlHash;
      attachments = [...getAttachmentsResp.attachments, { id: item.itemId, folderPaths: {}, name: item.subject, hash: emlHash, contentType: 'message/rfc822' }];
    }
  }

  // check and set assumed saved/presaved file count, this largely governs if attachments
  // have been removed and we have more files saved/presaved than attached to the eml.
  let fileCount = (emailAndDisplayRecords.preSave && emailAndDisplayRecords?.relatedTos?.reduce((acc, curValue) => {
    const attachmentsOnRelatedRecord = [];
    for (const attachment of curValue.attachments.fileAttachments) {
      if (acc.includes(attachment.hash)) {
        continue;
      }
      attachmentsOnRelatedRecord.push(attachment.hash); // this allows a second attachment on a single related record but on different records considers them the same
    }
    acc.push(...attachmentsOnRelatedRecord);
    return acc;
  }, []).length) || 0;

  if (emailAndDisplayRecords.preSave || emailAndDisplayRecords.emailMessageRecordSaved || Object.values(emailAndDisplayRecords.Results || {}).some((result) => result.emailMessageRecordSaved)) {
    fileCount += 1;
  }

  // if the associated token is cancelled, we do not do any dispatches.
  const tokenValue = tokenManager.getTokenValue(tokenId);
  if (!tokenValue) {
    batch(() => {
      if (!item.itemId) {
        if (emailAndDisplayRecords.preSave) {
          dispatch(MailActions.setTimelineSubject(subject));
          const docrioSubject = emailAndDisplayRecords?.relatedTos.find((relatedRecord) => relatedRecord?.attachments?.eml?.Name)?.attachments?.eml?.Name?.replace(/\.[^/.]+$/, '');
          dispatch(MailActions.setDocrioSubject(docrioSubject || subject));
        }
        dispatch(MailActions.composingEmail(draftId, emailAndDisplayRecords.preSave));
      } else {
        if (emailAndDisplayRecords?.EmailMessage || emailAndDisplayRecords?.Results) {
          dispatch(MailActions.setTimelineSubject(emailMessageRecordName));
          dispatch(MailActions.setDocrioSubject(docrioSubject));
        }
        if (emailAndDisplayRecords.jobResults?.length) {
          dispatch(FileActions.setFilesSaveJobs(emailAndDisplayRecords.jobResults));
        }
        if (emailAndDisplayRecords.removalJobId) {
          dispatch(MailActions.setRemovalJobId(emailAndDisplayRecords.removalJobId));
        }
        dispatch(MailActions.emailLoaded(item));
      }
      dispatch(RootActions.setInitializationError(false));
      // if no saved eml subject is found assume the outlook/item subject.
      dispatch(MailActions.setSubject(emlSubject || subject));
      dispatch(MailActions.setBody(body));
      dispatch(FileActions.setDocrioFiles(attachments));
      dispatch(FileActions.setSavedCount(fileCount));
      dispatch(TimeActions.setTimeEnabled(timeEnabled));
      dispatch(AuthActions.loginSuccess());
      dispatch(RootActions.officeInitialized());
      dispatch(SalesforceRecordActions.setCachedObjectFieldSets(cachedObjectFormFields));
    });
  }
  tokenManager.removeToken(tokenId);
  resolve();
};

// This function is used to check if the object form fields are cached in the local storage
const checkForCachedObjectFormFields = async () => {
  const cachedObjectFormFields = getLocalStorageWithExpiry(CACHED_OBJECT_FORM_FIELDS_STRING);
  if (cachedObjectFormFields) {
    return cachedObjectFormFields;
  }
  return {};
};
